/**
 * LTX family descriptors (Phase 1, unit U5): ltx-2.3, ltx-2.3-fast, ltx-2, ltx-2-fast.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * P1 is price- and payload-neutral: every value below reproduces what the legacy paths send and
 * reserve TODAY, quirks included, pinned by U0's goldens (families/ltx.parity.test.ts):
 *   editor   server/routes/falai.ts  reserveSeconds :1378-1383, case 'ltx-2.3'/'ltx-2.3-fast' :1610-1655,
 *                                    case 'ltx-2'/'ltx-2-fast' :1657-1689
 *   headless server/services/ProviderService.ts  headlessFalVideoSeconds :1303, generic shape :1333-1346,
 *                                    LTX-2.3 branch :1483-1505 (LTX-2 has NO branch: generic shape only)
 *   price    server/services/CreditService.ts  ltx-2.3 / ltx-2.3-t2v table :1336-1363,
 *                                    LTX_23_FAST_RATES :1393-1402, generic tail :1468-1475 (ltx-2*)
 *
 * Schemas: __schemas__/fal-ai__ltx-2.3__*.json, fal-ai__ltx-2-19b__*.json (oracle 2026-09-19).
 */
import type { ModelDescriptor, Param } from '../types';
import { IMG, W_IMG } from '../_shared';
import { RESOLUTION_SPELLINGS, ltx23ProAliases } from './_u5Resolutions';

const SCHEMA_DATE = '2026-09-19';
const VENDOR_CHECKED = '2026-09-19';

/** Legacy `if (enableAudio && …)` is JS truthiness: these spellings reach the wire as `true` too. */
const TRUTHY_AUDIO = { true: true, '1': true, yes: true } as const;

const enableAudioParam: Param = {
  id: 'enableAudio', type: 'bool', default: false, aliases: TRUTHY_AUDIO,
  invalid: { coerce: { kind: 'default' } },
  // Emitted only by the editor (legacy.wire below): the headless builder never sends
  // generate_audio for LTX (falVideoCreditModelId has no LTX audio SKU, ProviderService.ts:1333).
  wire: false,
  ui: { label: 'Generate audio', tier: 'basic' },
};

/** Editor-only `generate_audio: true` when audio is on (falai.ts:1650 / :1686); omitted when off. */
const editorAudioWire = {
  field: 'generate_audio', fromParam: 'enableAudio', map: { true: true }, surface: 'editor',
  why: 'editor sends generate_audio only when enableAudio is truthy; headless never sends it (ProviderService.ts:1333). Moves to the param wire when headless converges (plan Q4/Q6).',
  retire: 'owner-approval',
} as const;

const audioOmit = {
  field: 'generate_audio',
  why: 'audio off -> field omitted, and fal defaults generate_audio to TRUE, so every LTX clip renders WITH audio. Sending false is plan Q4 (owner approval).',
};

/** LTX-2.3 aspect ratio: `aspectRatio === '9:16' ? '9:16' : '16:9'` on both surfaces, both modes. */
const ltx23AspectRatio: Param = {
  id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16'], default: '16:9',
  invalid: { coerce: { kind: 'default' } },
  wire: { field: 'aspect_ratio' },
  ui: { label: 'Aspect ratio', tier: 'basic' },
};

// ─── LTX-2.3 Pro ─────────────────────────────────────────────────────────────────────────────

/**
 * LTX-2.3 Pro resolution. Legacy prices and renders from DIFFERENT readings of the raw value:
 *   price  (CreditService.ts:1354)  falsy -> 1080p; '720p'|'1080p' -> 1080p; '1440p';
 *                                   '4k' or includes('2160'|'4k'|'4K') -> 2160p; else 1080p
 *   editor wire (falai.ts:1640)     '720p'|'1080p' -> 1080p; '1440p'; exactly '2160p'|'4k' -> 2160p; else 1080p
 *   headless wire (ProviderService.ts:1500) '1440p'; '2160p' or includes('2160') or lower includes('4k') -> 2160p; else 1080p
 * so a spelling like '4K' or '2160P' BILLS the 2160p rate but the editor RENDERS 1080p. The
 * canonical value '4K' is that class: 148 cr/s, editor wire 1080p, headless wire 2160p.
 * Case-sensitive on purpose (no `normalize`): '1440P' is 1080p everywhere.
 *
 * REVIEW ROUND 1, defect 1. The three readings are substring rules over the RAW string, which
 * the headless REST API forwards untouched; the frozen vocabulary has no substring classifier
 * (see fal/_u5Resolutions.ts). The alias map is therefore DERIVED by running the three rules,
 * transcribed once, over the verified spelling corpus — never hand-fitted to the U0 fixtures —
 * and every entry it produces is compared against the real CreditService by pricingParity
 * (pricingDomain unions `values ∪ uiValues ∪ aliases`). The residual (a string containing
 * '2160' or '4k'/'4K' that is outside the corpus: descriptor 37 cr/s, legacy 148) is enumerated
 * and pinned by ltx.parity.test.ts and escalated in the U5 report; it needs one additive rule
 * in types.ts, which this unit may not edit.
 */
const ltx23Resolution: Param = {
  id: 'resolution', type: 'enum', values: ['1080p', '1440p', '2160p', '4K'], uiValues: ['1080p', '1440p', '2160p'],
  aliases: ltx23ProAliases(),
  default: '1080p', role: 'tier', invalid: { coerce: { kind: 'default' } },
  wire: { field: 'resolution', map: { '4K': '1080p' } },            // the editor rule; headless overrides below
  ui: { label: 'Resolution', tier: 'basic', optionLabels: { '2160p': '2160p (4K)' } },
};

const LTX23_TABLE = { '1080p': 37, '1440p': 74, '2160p': 148, '4K': 148 } as const;
const LTX23_USD = { '1080p': 0.08, '1440p': 0.16, '2160p': 0.32, '4K': 0.32 } as const;
const LTX23_TEXT = 'Your request will cost $0.08 per second for 1080p, $0.16 per second for 1440p or $0.32 per second for 2160p.';

export const LTX_23: ModelDescriptor = {
  id: 'ltx-2.3', provider: 'falai', face: 'video', displayName: 'LTX-2.3 Pro', ledgerName: 'LTX-2.3', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/ltx-2.3/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'fal-ai/ltx-2.3/image-to-video', ports: [IMG()], media: { image: W_IMG } },
    // end_image_url exists on the i2v schema; legacy never sends it -> P2.
  },
  params: [
    // clampLtx23Duration(d, false): max(6, min(10, round((Number(d) || 6) / 2) * 2)). Snapping to
    // the even enum with ties UP is the same function over every number (Math.round rounds .5 up).
    { id: 'duration', type: 'int', values: [6, 8, 10], uiValues: [6, 8, 10], default: 6, role: 'units', normalize: 'trim',
      invalid: { coerce: { kind: 'snap', values: [6, 8, 10], ties: 'up' } },
      wire: { field: 'duration', as: 'integer' }, ui: { label: 'Duration', tier: 'basic' } },
    ltx23Resolution,
    ltx23AspectRatio,
    enableAudioParam,
  ],
  fixed: { fps: 25 },
  legacy: {
    wire: [
      editorAudioWire,
      { field: 'resolution', fromParam: 'resolution', surface: 'headless',
        map: { '1080p': '1080p', '1440p': '1440p', '2160p': '2160p', '4K': '2160p' },
        why: "headless reads '4K'/'2160P' as 2160p (includes/toLowerCase, ProviderService.ts:1500) where the editor renders them at 1080p (falai.ts:1640); the price is 2160p on both",
        retire: 'owner-approval' },
    ],
    omits: [audioOmit],
    notes: [
      "SKU: legacy reserves 'ltx-2.3-t2v' when falai.ts isTextToVideo holds (mode 'text-to-video', or no image AND no last image). The route posts to the t2v endpoint in more cases than that (i2v with a last image, a garbage mode with an image; FLF on a non-FLF model is a pre-reserve 400 since 2860433): those reserve 'ltx-2.3' (same 37/74/148 rate) while this descriptor, which only sees the mode, picks 'ltx-2.3-t2v'. Ledger-only difference; needs the editor mapper to forward isTextToVideo (see the U5 report).",
      'Editor first-last-frame with two images is refused 400 before reserving (falai.ts, 2860433: the VEO-shaped branch that posted it to the T2V endpoint was retired with the fal.ai VEO 3.1 model); the engine refuses it in resolve() (no flf mode, UNSUPPORTED_MODE).',
    ],
  },
  pricing: {
    unit: 'second', units: { param: 'duration', stage: 'resolved' }, rounding: 'table-min1', policy: 'fal-video-35',
    skus: [
      // falVideoCreditModelId keys the split on the route's isTextToVideo, not the endpoint ($t2v).
      { id: 'ltx-2.3-t2v', when: { $t2v: { eq: true } },
        rate: { by: ['resolution'], table: LTX23_TABLE, fallbackKey: '1080p' },
        vendor: { usd: LTX23_USD, per: 'second', field: 'pricingInfoOverride', text: LTX23_TEXT,
                  source: 'fal-ai/ltx-2.3/text-to-video (migration 129: t2v and i2v cost the same)', verified: VENDOR_CHECKED },
        registry: { displayName: 'LTX-2.3 Pro (Text-to-Video)', operationType: 'video', publishedTier: '1080p', flatRate: false } },
      { id: 'ltx-2.3',
        rate: { by: ['resolution'], table: LTX23_TABLE, fallbackKey: '1080p' },
        vendor: { usd: LTX23_USD, per: 'second', field: 'pricingInfoOverride', text: LTX23_TEXT,
                  source: 'fal-ai/ltx-2.3/image-to-video (migration 129)', verified: VENDOR_CHECKED },
        registry: { displayName: 'LTX-2.3 Pro', operationType: 'video', publishedTier: '1080p', flatRate: true } },
    ],
  },
  knowledge: { summary: 'LTX-2.3 Pro: 6/8/10 s at 1080p/1440p/2160p, 25 fps. Audio is rendered unless generate_audio=false is sent (legacy never sends false).' },
  schemaVerified: SCHEMA_DATE,
};

// ─── LTX-2.3 Fast ────────────────────────────────────────────────────────────────────────────

export const LTX_23_FAST: ModelDescriptor = {
  id: 'ltx-2.3-fast', provider: 'falai', face: 'video', displayName: 'LTX-2.3 Fast', ledgerName: 'LTX-2.3 Fast', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/ltx-2.3/text-to-video/fast', ports: [], media: {} },
    i2v: { endpoint: 'fal-ai/ltx-2.3/image-to-video/fast', ports: [IMG()], media: { image: W_IMG } },
  },
  params: [
    // clampLtx23Duration(d, true): even values 6..20, ties up (see LTX_23).
    { id: 'duration', type: 'int', values: [6, 8, 10, 12, 14, 16, 18, 20], uiValues: [6, 8, 10, 12, 14, 16, 18, 20], default: 6,
      role: 'units', normalize: 'trim',
      invalid: { coerce: { kind: 'snap', values: [6, 8, 10, 12, 14, 16, 18, 20], ties: 'up' } },
      wire: { field: 'duration', as: 'integer' }, ui: { label: 'Duration', tier: 'basic' } },
    // effectiveLtx23FastResolution (falModels.ts:511): trim + lowercase, '4k' -> 2160p, else 1080p;
    // the SAME function prices (CreditService.ts:1398) and renders (falai.ts:1638, ProviderService.ts:1498).
    { id: 'resolution', type: 'enum', values: ['1080p', '1440p', '2160p'], aliases: { '4k': '2160p' }, normalize: 'lower',
      default: '1080p', role: 'tier', invalid: { coerce: { kind: 'default' } }, wire: { field: 'resolution' },
      ui: { label: 'Resolution', tier: 'basic', optionLabels: { '2160p': '2160p (4K)' } } },
    ltx23AspectRatio,
    enableAudioParam,
  ],
  fixed: { fps: 25 },
  constraints: [{ id: 'ltx23-fast-long-clip-1080p', when: { duration: { gt: 10 } }, force: { resolution: '1080p' },
    why: '>10s only at 25 FPS and 1080p; billed at 1080p too (effectiveLtx23FastResolution)',
    source: 'fal OpenAPI duration.description (oracle 2026-09-19)' }],
  legacy: { wire: [editorAudioWire], omits: [audioOmit] },
  pricing: {
    unit: 'second', units: { param: 'duration', stage: 'resolved' }, rounding: 'table-min1', policy: 'fal-video-35',
    skus: [{ id: 'ltx-2.3-fast', rate: { by: ['resolution'], table: { '1080p': 28, '1440p': 56, '2160p': 111 }, fallbackKey: '1080p' },
      vendor: { usd: { '1080p': 0.06, '1440p': 0.12, '2160p': 0.24 }, per: 'second', field: 'pricingInfoOverride',
                text: 'Your request will cost $0.06 per second for 1080p, $0.12 per second for 1440p or $0.24 per second for 2160p.',
                source: 'fal-ai/ltx-2.3/text-to-video/fast (migration 128; the $0.04 README table is stale)', verified: VENDOR_CHECKED },
      registry: { displayName: 'LTX-2.3 Fast', operationType: 'video', publishedTier: '1080p', flatRate: true } }],
  },
  knowledge: { summary: 'LTX-2.3 Fast: 6-20 s; above 10 s only at 1080p (priced at 1080p too). 25 fps.' },
  schemaVerified: SCHEMA_DATE,
};

// ─── LTX-2 (19B) and LTX-2 Fast (distilled): hidden ──────────────────────────────────────────

/**
 * The LTX-2 19B schemas have NO duration, resolution or aspect_ratio (length = num_frames,
 * size = video_size). Legacy still sends all three: the editor its own mapping, headless the
 * generic { duration, aspect_ratio } shape. They are reproduced as legacy.wire (dead keys, B2).
 *
 * The PRICE is CreditService's generic tail over the RAW request: ceil(rate × ceil(units) ×
 * ladder(resolution)), flat_rate false — i.e. `legacy-resolution-v1` applied to the RAW string.
 *
 * REVIEW ROUND 1, defect 1. This was a canonical enum with a hand-fitted alias list, so a
 * spelling outside it ('1920x1080', '3840x2160', '2160p60', ' 4k', '1080') fell to the default
 * and billed 1x where legacy bills 1.5x or 3x. It is now `invalid: 'passthrough'` — exactly what
 * pixverse.ts does — so the ladder classifies the raw value itself and the price is EXACT for
 * every string, with no corpus involved.
 *
 * The editor payload rule (falai.ts:1670-1682) is exact-match with an else-branch, so it is
 * reproduced by two ORDERED legacy.wire entries: a constant '1080p', then an exact map that
 * overwrites it for '1440p' / '2160p' / '4k'. That is total over every string too.
 *
 * The aliases below are IDENTITIES. They change nothing at runtime (applyAlias returns the same
 * value, and passthrough would have kept it anyway); they exist so that pricingHarness's
 * `pricingDomain`, which unions `values ∪ uiValues ∪ aliases`, compares each verified spelling
 * against the real CreditService.
 */
const ltx2Resolution: Param = {
  id: 'resolution', type: 'string',
  aliases: Object.fromEntries(RESOLUTION_SPELLINGS.map((s) => [s, s])),
  default: '720p', role: 'tier', invalid: 'passthrough', wire: false,
  ui: { label: 'Resolution', tier: 'hidden' },
};

const ltx2 = (fast: boolean): ModelDescriptor => {
  const id = fast ? 'ltx-2-fast' : 'ltx-2';
  const name = fast ? 'LTX-2 Fast' : 'LTX-2';
  const base = fast ? 'fal-ai/ltx-2-19b/distilled' : 'fal-ai/ltx-2-19b';
  const usd = fast ? 0.0008 : 0.0018;
  return {
    id, provider: 'falai', face: 'video', displayName: name, ledgerName: name, status: 'hidden',
    prompt: { field: 'prompt', required: true },
    modes: {
      t2v: { endpoint: `${base}/text-to-video`, ports: [], media: {} },
      i2v: { endpoint: `${base}/image-to-video`, ports: [IMG()], media: { image: W_IMG } },
    },
    params: [
      // Editor: `d <= 6 ? 6 : d <= 8 ? 8 : 10` over the raw body value; headless: the raw
      // `Number(d) || 8` seconds. Priced on the RAW value either way (stage 'raw').
      // 5 is in the domain only because it is the route's destructuring default (R5), which is
      // what an absent duration BILLS (5 s); the editor wire maps it to 6 (legacy.wire below).
      { id: 'duration', type: 'int', values: [5, 6, 8, 10], default: 5, role: 'units',
        invalid: { editor: { coerce: { kind: 'snap', values: [5, 6, 8, 10], ties: 'up' } }, headless: 'passthrough' },
        wire: false, ui: { label: 'Duration', tier: 'basic' } },
      ltx2Resolution,
      { id: 'aspectRatio', type: 'string', default: '16:9', invalid: 'passthrough', wire: false,
        ui: { label: 'Aspect ratio', tier: 'hidden' } },
      enableAudioParam,
    ],
    legacy: {
      wire: [
        { field: 'duration', fromParam: 'duration', surface: 'editor', map: { 5: 6, 6: 6, 8: 8, 10: 10 },
          why: `editor 6/8/10 mapping (falai.ts:1661); absent from the ${name} schemas (length is num_frames; oracle ${SCHEMA_DATE})`,
          retire: 'inert-cleanup' },
        { field: 'duration', fromParam: 'duration', surface: 'headless',
          why: `headless generic shape sends the raw seconds (ProviderService.ts:1335); absent from the ${name} schemas`,
          retire: 'inert-cleanup' },
        { field: 'aspect_ratio', value: '16:9', surface: 'editor',
          why: `editor constant (falai.ts:1663); absent from the ${name} schemas`, retire: 'inert-cleanup' },
        { field: 'aspect_ratio', fromParam: 'aspectRatio', surface: 'headless',
          why: `headless generic shape (ProviderService.ts:1336); absent from the ${name} schemas`, retire: 'inert-cleanup' },
        // Ordered pair: the else-branch first, then the three exact arms that overwrite it.
        // Together they are the editor rule for EVERY string, not just enumerated ones.
        { field: 'resolution', value: '1080p', surface: 'editor',
          why: `editor else-branch (falai.ts:1681): anything the exact arms below do not match renders 1080p; absent from the ${name} schemas (size is video_size)`,
          retire: 'inert-cleanup' },
        { field: 'resolution', fromParam: 'resolution', surface: 'editor',
          map: { '1440p': '1440p', '2160p': '2160p', '4k': '2160p' },
          why: `editor exact arms (falai.ts:1674-1678) over the raw value; overwrites the 1080p default above`,
          retire: 'inert-cleanup' },
        { field: 'fps', value: 25, surface: 'editor', why: 'editor constant (falai.ts:1685); headless sends none', retire: 'owner-approval' },
        editorAudioWire,
      ],
      omits: [
        audioOmit,
        { field: 'num_frames', why: 'never sent: fal renders its default 121 frames (~4.8 s at 25 fps) whatever duration was billed' },
        { field: 'video_quality', why: 'never sent (fal default "high")' },
      ],
      notes: [
        'Removed from the LTX dropdown in migration 129; saved flows and headless callers still reach it, so the row stays active.',
        "CLOSED by d01af92: editor duration 'abc' used to reserve NaN units (Math.ceil('abc')). The route now replaces an unusable duration with 5 and bills ltx2Seconds() of it; the editor mapper hands the engine the same value, and the engine bills an unusable raw units value at the value it resolves.",
      ],
    },
    pricing: {
      unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'generic', policy: 'fal-video-35',
      skus: [{ id, rate: { ladder: { base: fast ? 14 : 28, by: 'resolution', curve: 'legacy-resolution-v1' } },
        vendor: { usd, per: 'megapixel', field: 'pricingInfoOverride',
                  text: `Your request will cost $${usd} per megapixel of generated video data (width × height × frames), rounded up.`,
                  source: `${base}/text-to-video`, verified: VENDOR_CHECKED },
        marginException: 'fal bills per megapixel of generated video; that cannot map onto a per-second row (CLAUDE.md). The live row bills per requested second while fal renders num_frames=121 at video_size default, so the charge does not track the cost. Kept as-is in P1.',
        registry: { displayName: name, operationType: 'video', flatRate: false } }],
    },
    knowledge: { summary: `${name} (19B): deprecated by fal in favour of LTX-2.3; hidden from the picker.` },
    schemaVerified: SCHEMA_DATE,
  };
};

export const LTX_2 = ltx2(false);
export const LTX_2_FAST = ltx2(true);

export const LTX_MODELS: readonly ModelDescriptor[] = [LTX_23, LTX_23_FAST, LTX_2, LTX_2_FAST];
